import { Box, InputBase } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';

import { DropdownOption, FilterBarLabels, FilterState, OptionGroup } from './FilterBar.props';
import { controlEngagedSx } from './filterBarStyles';
import { MultiSelectDropdown } from './MultiSelectDropdown';

export interface FilterBarBuiltInFieldsProps {
  filters: FilterState;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  statuses: DropdownOption[];
  statusGroups?: OptionGroup[];
  jobTypes: DropdownOption[];
  contracts: DropdownOption[];
  contractNames: DropdownOption[];
  resolvedLabels: Required<
    Pick<FilterBarLabels, 'statusesDropdown' | 'jobTypesDropdown' | 'contractsDropdown' | 'idField'>
  > &
    Pick<
      FilterBarLabels,
      'extraTextPlaceholder' | 'hideIdField' | 'hideContractsDropdown' | 'contractNamesDropdown'
    >;
  inputSx: React.CSSProperties | Record<string, unknown>;
  theme: Theme;
}

/** Built-in dropdowns + text inputs surfaced when `showBuiltInFields` is
 *  true. Extracted from `FilterBar` to keep the parent component under the
 *  project's 250-line file budget. */
export const FilterBarBuiltInFields: React.FC<FilterBarBuiltInFieldsProps> = ({
  filters,
  updateFilter,
  statuses,
  statusGroups,
  jobTypes,
  contracts,
  contractNames,
  resolvedLabels,
  inputSx,
  theme,
}) => (
  <>
    <MultiSelectDropdown
      label={resolvedLabels.statusesDropdown}
      options={statuses}
      selected={filters.statuses}
      onChange={(val) => updateFilter('statuses', val)}
      groups={statusGroups}
    />
    <MultiSelectDropdown
      label={resolvedLabels.jobTypesDropdown}
      options={jobTypes}
      selected={filters.jobTypes}
      onChange={(val) => updateFilter('jobTypes', val)}
    />
    {!resolvedLabels.hideContractsDropdown && (
      <MultiSelectDropdown
        label={resolvedLabels.contractsDropdown}
        options={contracts}
        selected={filters.contracts}
        onChange={(val) => updateFilter('contracts', val)}
      />
    )}
    {resolvedLabels.contractNamesDropdown && (
      <MultiSelectDropdown
        label={resolvedLabels.contractNamesDropdown}
        options={contractNames}
        selected={filters.contractNames}
        onChange={(val) => updateFilter('contractNames', val)}
      />
    )}

    {resolvedLabels.extraTextPlaceholder && (
      <InputBase
        placeholder={resolvedLabels.extraTextPlaceholder}
        value={filters.extraText}
        onChange={(e) => updateFilter('extraText', e.target.value)}
        inputProps={{ 'aria-label': `${resolvedLabels.extraTextPlaceholder} filter` }}
        sx={
          {
            ...inputSx,
            ...(filters.extraText ? controlEngagedSx(theme) : {}),
            width: 180,
          } as React.ComponentProps<typeof Box>['sx']
        }
      />
    )}

    {!resolvedLabels.hideIdField && (
      <InputBase
        placeholder={resolvedLabels.idField}
        value={filters.jobId}
        onChange={(e) => updateFilter('jobId', e.target.value)}
        inputProps={{ 'aria-label': `${resolvedLabels.idField} filter` }}
        sx={
          {
            ...inputSx,
            ...(filters.jobId ? controlEngagedSx(theme) : {}),
            width: 120,
          } as React.ComponentProps<typeof Box>['sx']
        }
      />
    )}
  </>
);
